import type { Types } from '@cornerstonejs/core';
import { Enums, RenderingEngine } from '@cornerstonejs/core';
import * as cornerstoneTools from '@cornerstonejs/tools';
import {
  addButtonToToolbar,
  addSliderToToolbar,
  addDropdownToToolbar,
  addToggleButtonToToolbar,
  createDisplaySets,
  getViewportTypeForDisplaySet,
  createInfoSection,
  initDemo,
  setTitleAndDescription,
  addManipulationBindings,
  getLocalUrl,
  addVideoTime,
  addSegmentIndexDropdown,
  contourTools,
} from '../../../../utils/demo/helpers';
import type { Types as cstTypes } from '@cornerstonejs/tools';

// This is for debugging purposes
console.warn(
  'Click on index.ts to open source code for this example --------->'
);

const DEFAULT_SEGMENTATION_CONFIG = {
  fillAlpha: 0.5,
  fillAlphaInactive: 0.3,
  outlineOpacity: 1,
  outlineOpacityInactive: 0.85,
  outlineWidth: 3,
  outlineWidthInactive: 2,
  outlineDash: undefined,
  outlineDashInactive: undefined,
};

const {
  ToolGroupManager,
  Enums: csToolsEnums,
  segmentation,
} = cornerstoneTools;

// Define various constants for the tool definition
const toolGroupId = 'DEFAULT_TOOLGROUP_ID';

const segmentationId = `SEGMENTATION_ID`;
const segmentIndexes = [1, 2, 3, 4, 5];
const segmentVisibilityMap = new Map();

const { toolMap } = contourTools;

// ======== Set up page ======== //

setTitleAndDescription(
  'Video Contour Segmentation Tools',
  'Here we demonstrate how to use spline and livewire segmentation ROI tools on a video viewport'
);

const size = '512px';
const content = document.getElementById('content');
const viewportGrid = document.createElement('div');
let viewport;

viewportGrid.style.display = 'flex';
viewportGrid.style.flexDirection = 'row';

const viewportId = 'VIDEO_VIEWPORT_ID';
const element = document.createElement('div');

element.oncontextmenu = () => false;
element.style.width = size;
element.style.height = size;

viewportGrid.appendChild(element);

content.appendChild(viewportGrid);

createInfoSection(content, { ordered: true })
  .addInstruction('Select a segmentation index')
  .addInstruction('Select a spline curve type')
  .addInstruction('Draw a spline curve on the viewport')
  .addInstruction('Repeat the steps 1-3 as many times as you want')
  .addInstruction(
    'Notice that each segment index has a different color assigned to it'
  )
  .addInstruction('Change the style for the segmentation')
  .addInstruction('Confirm the style is applied properly');

function updateInputsForCurrentSegmentation() {
  // We can use any toolGroupId because they are all configured in the same way
  const segmentationConfig = getSegmentationConfig(toolGroupId);
  const contourConfig = segmentationConfig.Contour;

  (document.getElementById('outlineWidth') as HTMLInputElement).value = String(
    contourConfig.outlineWidth ?? DEFAULT_SEGMENTATION_CONFIG.outlineWidth
  );

  (document.getElementById('outlineOpacity') as HTMLInputElement).value =
    String(
      contourConfig.outlineOpacity ?? DEFAULT_SEGMENTATION_CONFIG.outlineOpacity
    );

  (document.getElementById('fillAlpha') as HTMLInputElement).value = String(
    contourConfig.fillAlpha ?? DEFAULT_SEGMENTATION_CONFIG.fillAlpha
  );
}

function getSegmentsVisibilityState() {
  let segmentsVisibility = segmentVisibilityMap.get(segmentationId);

  if (!segmentsVisibility) {
    segmentsVisibility = new Array(segmentIndexes.length + 1).fill(true);
    segmentVisibilityMap.set(segmentationId, segmentsVisibility);
  }

  return segmentsVisibility;
}

function getSegmentationConfig(
  toolGroupId: string
): cstTypes.RepresentationConfig {
  const segmentationConfig =
    segmentation.config.getSegmentationRepresentationConfig(
      segmentationRepresentationUID
    ) ?? {};

  // Add Contour object because it
  // can return an empty object
  if (!segmentationConfig.Contour) {
    segmentationConfig.Contour = {};
  }

  return segmentationConfig;
}

function updateSegmentationConfig(config) {
  const segmentationConfig = getSegmentationConfig(toolGroupId);

  Object.assign(segmentationConfig.Contour, config);

  segmentation.config.setSegmentationRepresentationConfig(
    segmentationRepresentationUID,
    segmentationConfig
  );
}

// ============================= //

const cancelDrawingEventListener = (evt) => {
  const { element, key } = evt.detail;
  if (key === 'Escape') {
    cornerstoneTools.cancelActiveManipulations(element);
  }
};

element.addEventListener(
  csToolsEnums.Events.KEY_DOWN,
  cancelDrawingEventListener
);

addSegmentIndexDropdown(segmentationId);

addDropdownToToolbar({
  options: { map: toolMap },
  toolGroupId,
});

addToggleButtonToToolbar({
  title: 'Show/Hide All Segments',
  onClick: function (toggle) {
    const segmentsVisibility = getSegmentsVisibilityState();

    segmentation.config.visibility.setSegmentationRepresentationVisibility(
      viewportId,
      {
        segmentationId,
        type: csToolsEnums.SegmentationRepresentations.Contour,
      },
      !toggle
    );

    segmentsVisibility.fill(!toggle);
  },
});

addButtonToToolbar({
  title: 'Show/Hide Current Segment',
  onClick: function () {
    const segmentsVisibility = getSegmentsVisibilityState();
    const { segmentIndex: activeSegmentIndex } = addSegmentIndexDropdown;
    const visible = !segmentsVisibility[activeSegmentIndex];

    segmentation.config.visibility.setSegmentIndexVisibility(
      viewportId,
      {
        segmentationId,
        type: csToolsEnums.SegmentationRepresentations.Contour,
      },
      activeSegmentIndex,
      visible
    );

    segmentsVisibility[activeSegmentIndex] = visible;
  },
});

addSliderToToolbar({
  id: 'outlineWidth',
  title: 'Outline Thickness',
  range: [0.1, 10],
  step: 0.1,
  defaultValue: 1,
  onSelectedValueChange: (value) => {
    updateSegmentationConfig({ outlineWidth: Number(value) });
  },
});

addSliderToToolbar({
  id: 'outlineOpacity',
  title: 'Outline Opacity',
  range: [0, 1],
  step: 0.05,
  defaultValue: 1,
  onSelectedValueChange: (value) => {
    updateSegmentationConfig({ outlineOpacity: Number(value) });
  },
});

addSliderToToolbar({
  id: 'fillAlpha',
  title: 'Fill Alpha',
  range: [0, 1],
  step: 0.05,
  defaultValue: 0.5,
  onSelectedValueChange: (value) => {
    updateSegmentationConfig({ fillAlpha: Number(value) });
  },
});

/**
 * Runs the demo
 */
async function run() {
  // Init Cornerstone and related libraries
  await initDemo();

  // Define tool groups to add the segmentation display tool to
  const toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
  addManipulationBindings(toolGroup, { toolMap });

  // Get Cornerstone imageIds and fetch metadata into RAM
  const displaySets = await createDisplaySets({
    StudyInstanceUID: '2.25.96975534054447904995905761963464388233',
    SeriesInstanceUID: '2.25.15054212212536476297201250326674987992',
    wadoRsRoot:
      getLocalUrl() || 'https://d14fa38qiwhyfd.cloudfront.net/dicomweb',
  });

  const displaySet =
    displaySets.find((ds) => ds.preferredViewportType === 'video') ??
    displaySets[0];
  if (!displaySet) {
    throw new Error('No display set found in series');
  }
  const videoId = displaySet.instances[0].imageId;

  // Instantiate a rendering engine
  const renderingEngineId = 'myRenderingEngine';
  const renderingEngine = new RenderingEngine(renderingEngineId);

  // Create the viewports
  const viewportInputArray = [
    {
      viewportId: viewportId,
      type: getViewportTypeForDisplaySet(displaySet),
      element: element,
      defaultOptions: {
        background: <Types.Point3>[0.2, 0, 0.2],
      },
    },
  ];

  renderingEngine.setViewports(viewportInputArray);
  toolGroup.addViewport(viewportId, renderingEngineId);

  // Get the stack viewport that was created
  viewport = <Types.IStackViewport>renderingEngine.getViewport(viewportId);
  addVideoTime(viewportGrid, viewport);

  // Set the stack on the viewport
  await viewport.setDisplaySets({ displaySetId: videoId });

  // Render the image
  renderingEngine.render();

  // Add a segmentation that will contains the contour annotations
  segmentation.addSegmentations([
    {
      segmentationId,
      representation: {
        type: csToolsEnums.SegmentationRepresentations.Contour,
      },
    },
  ]);

  // Create a segmentation representation associated to the viewportId
  const segmentationRepresentationUIDs =
    await segmentation.addSegmentationRepresentations(viewportId, [
      {
        segmentationId,
        type: csToolsEnums.SegmentationRepresentations.Contour,
      },
    ]);

  // Store the segmentation representation that was just created
  [segmentationRepresentationUID] = segmentationRepresentationUIDs;

  updateInputsForCurrentSegmentation();
}

run();
